(() => {
  const SELECTOR = '[data-infinite-shop]';
  const DESKTOP_QUERY = '(min-width: 750px)';
  const REDUCED_QUERY = '(prefers-reduced-motion: reduce)';
  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
  const mod = (value, size) => ((value % size) + size) % size;

  class InfiniteProductField {
    constructor(root) {
      if (window.gsap && window.Flip) window.gsap.registerPlugin(window.Flip);
      this.root = root;
      this.viewport = root.querySelector('[data-grid-viewport]');
      this.field = root.querySelector('[data-product-field]');
      this.template = root.querySelector('[data-product-template]');
      this.productView = root.querySelector('[data-product-view]');
      this.wordmark = root.querySelector('.infinite-shop__wordmark');
      this.viewImage = root.querySelector('[data-view-image]');
      this.viewStage = root.querySelector('[data-view-stage]');
      this.viewMedia = root.querySelector('[data-view-media]');
      this.viewTrack = root.querySelector('[data-view-gallery-track]');
      this.viewGalleryMeta = root.querySelector('[data-view-gallery-meta]');
      this.viewGalleryCurrent = root.querySelector('[data-view-gallery-current]');
      this.viewGalleryTotal = root.querySelector('[data-view-gallery-total]');
      this.viewGalleryHint = root.querySelector('[data-view-gallery-hint]');
      this.viewCartLink = root.querySelector('[data-view-cart-link]');
      this.viewRail = root.querySelector('.product-view__rail-content');
      this.mobilePurchase = root.querySelector('[data-view-mobile-purchase]');
      this.counter = root.querySelector('[data-product-counter]');
      this.searchForm = root.querySelector('[data-grid-search]');
      this.searchInput = root.querySelector('[data-grid-search-input]');
      this.searchStatus = root.querySelector('[data-grid-search-status]');
      this.searchEmpty = root.querySelector('[data-grid-search-empty]');
      this.dragHint = root.querySelector('[data-drag-hint]');
      this.searchToggle = root.querySelector('[data-grid-search-toggle]');
      this.flipLayer = document.createElement('div');
      this.flipLayer.className = 'product-view__flip-layer';
      this.flipLayer.setAttribute('aria-hidden', 'true');
      this.root.appendChild(this.flipLayer);
      this.allSources = [...this.template.content.querySelectorAll('[data-product-card]')];
      this.sources = [...this.allSources];
      this.shopUrl = root.dataset.shopUrl || '/';
      this.shopTitle = root.dataset.shopTitle || document.title;
      this.initialProductHandle = root.dataset.initialProductHandle || this.productHandleFromLocation();
      this.routeWasPushed = false;
      this.searchActive = false;
      this.searchQuery = '';
      this.searchPan = null;
      this.openSearchAfterClose = false;
      this.searchReveal = null;
      this.searchTimeline = null;
      this.searchTimer = 0;
      this.cards = [];
      this.active = null;
      this.introComplete = false;
      this.introTimeline = null;
      this.navTimeline = null;
      this.contentTimeline = null;
      this.imageFlip = null;
      this.closing = false;
      this.transitionReady = false;
      this.pushProgress = 0;
      this.galleryStep = 0;
      this.mobilePurchaseRaf = 0;
      this.previewCart = root.hasAttribute('data-preview-cart');
      this.cartState = this.previewCart ? { item_count: 0, items: [] } : null;
      this.cartStatusTimer = 0;
      this.galleryScroll = {
        current: 0,
        target: 0,
        raf: 0,
        settleTimer: 0,
        driving: false,
        previousTime: 0,
        ignoreNativeUntil: 0
      };
      this.galleryWheel = {
        active: false,
        resetTimer: 0,
        lockedUntil: 0
      };
      this.galleryDrag = null;
      this.pointer = { x: 0, y: 0, inside: false };
      this.pan = { x: 0, y: 0, tx: 0, ty: 0 };
      this.drag = null;
      this.suppressClickUntil = 0;
      this.settlingUntil = 0;
      this.raf = 0;
      this.resizeTimer = 0;
      this.dialogFocusTimer = 0;
      this.isDesktop = window.matchMedia(DESKTOP_QUERY).matches;
      this.reducedMotion = window.matchMedia(REDUCED_QUERY).matches;
      this.onResize = this.onResize.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.tick = this.tick.bind(this);
      this.onGalleryWheel = this.onGalleryWheel.bind(this);
      this.onGalleryScroll = this.onGalleryScroll.bind(this);
      this.onGalleryPointerMove = this.onGalleryPointerMove.bind(this);
      this.onPopState = this.onPopState.bind(this);
      this.onProductViewKeyDown = this.onProductViewKeyDown.bind(this);
      this.onProductViewScroll = this.onProductViewScroll.bind(this);
      this.bind();
      if (!this.sources.length) {
        this.root.classList.remove('is-intro');
        return;
      }
      this.root.classList.add('is-enhanced');
      // The non-enhanced fallback hides the interactive viewport. Reveal it
      // before measuring so the world is sized from the real viewport instead
      // of the five-column minimum.
      this.build();
      if (this.initialProductHandle) this.openInitialProduct(this.initialProductHandle);
      else this.runIntro();
    }

    productHandleFromLocation() {
      const match = window.location.pathname.match(/\/products\/([^/?#]+)/);
      return match ? decodeURIComponent(match[1]) : '';
    }

    modelHandle(model) {
      if (!model) return '';
      if (model.element.dataset.handle) return model.element.dataset.handle;
      try {
        return new URL(model.element.dataset.url || '', document.baseURI).pathname.match(/\/products\/([^/?#]+)/)?.[1] || '';
      } catch (_) {
        return '';
      }
    }

    findModelByHandle(handle) {
      const candidates = this.cards.filter((model) => this.modelHandle(model) === handle);
      if (!candidates.length) return null;
      if (!this.isDesktop) return candidates[0];
      const viewport = this.viewport.getBoundingClientRect();
      const cx = viewport.left + viewport.width / 2;
      const cy = viewport.top + viewport.height / 2;
      return candidates.reduce((nearest, model) => {
        const bounds = model.element.getBoundingClientRect();
        const distance = Math.hypot(bounds.left + bounds.width / 2 - cx, bounds.top + bounds.height / 2 - cy);
        return distance < nearest.distance ? { model, distance } : nearest;
      }, { model: candidates[0], distance: Infinity }).model;
    }

    openInitialProduct(handle) {
      this.introComplete = true;
      this.root.classList.remove('is-intro');
      this.root.style.removeProperty('--bar-width');
      requestAnimationFrame(() => this.openByHandle(handle, { updateHistory: false }));
    }

    openByHandle(handle, options = {}) {
      const model = this.findModelByHandle(handle);
      if (!model) return false;
      this.open(model, options);
      return true;
    }

    updateProductRoute(model) {
      const url = model?.element.dataset.url;
      if (!url) return;
      const target = new URL(url, document.baseURI);
      const current = new URL(window.location.href);
      if (target.pathname !== current.pathname || target.search !== current.search) {
        window.history.pushState({ infiniteShop: true, productHandle: this.modelHandle(model) }, '', `${target.pathname}${target.search}`);
        this.routeWasPushed = true;
      }
    }

    restoreShopRoute() {
      window.history.replaceState({ infiniteShop: true }, '', this.shopUrl);
      document.title = this.shopTitle;
    }

    onPopState(event) {
      const handle = this.productHandleFromLocation();
      if (handle) {
        this.routeWasPushed = Boolean(event.state?.infiniteShop);
        if (this.active && this.modelHandle(this.active) === handle) return;
        if (this.active) this.close(false, true, false);
        this.openByHandle(handle, { updateHistory: false });
        return;
      }
      this.routeWasPushed = false;
      document.title = this.shopTitle;
      if (this.active) this.close(true, false, false);
    }

    openSearch() {
      if (!this.introComplete) return;
      this.root.querySelector('.infinite-shop__mobile-menu')?.removeAttribute('open');
      if (this.active) {
        this.openSearchAfterClose = true;
        this.close(false);
        return;
      }
      if (this.searchActive) {
        this.searchInput?.focus();
        return;
      }
      this.searchActive = true;
      this.searchPan = { ...this.pan };
      this.searchToggle?.setAttribute('aria-expanded', 'true');
      const gsap = window.gsap;
      const chrome = [this.wordmark, this.root.querySelector('.infinite-shop__utilities')].filter(Boolean);
      const revealSearch = () => {
        this.root.classList.add('is-searching');
        this.searchForm?.setAttribute('aria-hidden', 'false');
      };
      const focusSearch = () => this.searchInput?.focus({ preventScroll: true });

      this.searchTimeline?.kill();
      if (!gsap || this.reducedMotion || !this.searchForm) {
        revealSearch();
        requestAnimationFrame(focusSearch);
        return;
      }

      gsap.set(this.searchForm, { autoAlpha: 0, x: 18 });
      this.searchTimeline = gsap.timeline({ defaults: { ease: 'circ.inOut' } });
      this.searchTimeline
        .to(chrome, { autoAlpha: 0, duration: .22 })
        .call(revealSearch)
        .to(this.searchForm, {
          autoAlpha: 1,
          x: 0,
          duration: .36,
          onComplete: focusSearch
        });
    }

    dismissSearchChrome(animate = true, onComplete = null) {
      this.searchActive = false;
      this.searchForm?.setAttribute('aria-hidden', 'true');
      this.searchToggle?.setAttribute('aria-expanded', 'false');
      const gsap = window.gsap;
      const chrome = [this.wordmark, this.root.querySelector('.infinite-shop__utilities')].filter(Boolean);
      const finish = () => {
        this.root.classList.remove('is-searching');
        if (gsap) {
          gsap.set(this.searchForm, { clearProps: 'opacity,visibility,transform' });
          gsap.set(chrome, { clearProps: 'opacity,visibility' });
        }
        onComplete?.();
      };

      this.searchTimeline?.kill();
      if (!animate || !gsap || this.reducedMotion || !this.searchForm) {
        finish();
        return;
      }

      this.searchTimeline = gsap.timeline({ defaults: { ease: 'circ.inOut' } });
      this.searchTimeline
        .to(this.searchForm, { autoAlpha: 0, x: 18, duration: .22 })
        .call(() => this.root.classList.remove('is-searching'))
        .fromTo(chrome, { autoAlpha: 0 }, {
          autoAlpha: 1,
          duration: .34,
          onComplete: finish
        });
    }

    closeSearch(restoreProducts = true, restoreFocus = true) {
      if (!this.searchActive && !this.searchQuery) return;
      this.dismissSearchChrome(true, restoreFocus
        ? () => this.searchToggle?.focus({ preventScroll: true })
        : null);
      if (restoreProducts) this.resetSearchProducts();
    }

    resetSearchProducts() {
      clearTimeout(this.searchTimer);
      const needsBuild = this.sources.length !== this.allSources.length || Boolean(this.searchQuery);
      this.searchQuery = '';
      if (this.searchInput) this.searchInput.value = '';
      this.sources = [...this.allSources];
      if (this.searchStatus) this.searchStatus.textContent = 'All products';
      if (this.searchEmpty) this.searchEmpty.hidden = true;
      if (needsBuild) {
        this.build();
        if (this.searchPan) {
          this.pan = { ...this.searchPan };
          this.searchPan = null;
        }
        this.revealSearchResults();
      } else {
        this.searchPan = null;
      }
    }

    filterProducts(value) {
      const query = value.trim().toLocaleLowerCase();
      this.searchQuery = query;
      this.sources = query
        ? this.allSources.filter((source) => {
          const text = [
            source.dataset.title,
            source.dataset.handle,
            source.dataset.productType,
            source.dataset.productTags,
            source.querySelector('.product-card__description')?.textContent
          ].filter(Boolean).join(' ').toLocaleLowerCase();
          return text.includes(query);
        })
        : [...this.allSources];
      const count = this.sources.length;
      if (this.searchStatus) this.searchStatus.textContent = query ? `${count} ${count === 1 ? 'product' : 'products'}` : 'All products';
      if (this.searchEmpty) this.searchEmpty.hidden = count > 0;
      this.build();
      this.revealSearchResults();
    }

    submitSearch() {
      if (!this.sources.length || !this.searchQuery) return;
      const handle = this.sources[0].dataset.handle;
      const model = handle ? this.findModelByHandle(handle) : this.cards[0];
      if (model) this.open(model);
    }

    revealSearchResults() {
      const gsap = window.gsap;
      const media = this.cards.map((card) => card.element.querySelector('.product-card__media')).filter(Boolean);
      if (!gsap || this.reducedMotion || !media.length) return;
      this.searchReveal?.kill();
      gsap.set(media, { scale: 0, transformOrigin: 'center center' });
      this.searchReveal = gsap.to(media, {
        scale: 1,
        duration: .5,
        stagger: { amount: .28, from: 'center' },
        ease: 'circ.out',
        clearProps: 'transform'
      });
    }

    bind() {
      this.viewport.addEventListener('pointerdown', (event) => this.onPointerDown(event));
      this.viewport.addEventListener('pointerenter', (event) => {
        this.pointer.inside = true;
        this.onPointerMove(event);
      });
      this.viewport.addEventListener('pointermove', this.onPointerMove);
      this.viewport.addEventListener('pointerup', (event) => this.onPointerUp(event));
      this.viewport.addEventListener('pointercancel', (event) => this.onPointerUp(event));
      this.viewport.addEventListener('pointerleave', () => {
        if (!this.drag) this.pointer.inside = false;
      });
      this.viewport.addEventListener('wheel', (event) => this.onWheel(event), { passive: false });
      this.viewStage?.addEventListener('wheel', this.onGalleryWheel, { passive: false });
      this.viewStage?.addEventListener('scroll', this.onGalleryScroll, { passive: true });
      this.viewStage?.addEventListener('pointerdown', (event) => this.onGalleryPointerDown(event));
      this.viewStage?.addEventListener('pointermove', this.onGalleryPointerMove);
      this.viewStage?.addEventListener('pointerup', (event) => this.onGalleryPointerUp(event));
      this.viewStage?.addEventListener('pointercancel', (event) => this.onGalleryPointerUp(event));
      this.viewport.addEventListener('keydown', (event) => this.onKeyDown(event));
      this.wordmark?.addEventListener('click', () => {
        if (this.active) this.close();
      });
      this.wordmark?.addEventListener('keydown', (event) => {
        if (!this.active || !['Enter', ' '].includes(event.key)) return;
        event.preventDefault();
        this.close();
      });
      this.searchToggle?.addEventListener('click', () => this.openSearch());
      this.searchForm?.querySelector('[data-grid-search-close]')?.addEventListener('click', () => this.closeSearch());
      this.searchForm?.addEventListener('submit', (event) => {
        event.preventDefault();
        this.submitSearch();
      });
      this.searchInput?.addEventListener('input', () => {
        clearTimeout(this.searchTimer);
        this.searchTimer = setTimeout(() => this.filterProducts(this.searchInput.value), 90);
      });
      this.searchInput?.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          this.closeSearch();
        } else if (event.key === 'Enter') {
          event.preventDefault();
          this.submitSearch();
        }
      });
      this.productView?.addEventListener('click', (event) => {
        if (!this.active || this.closing || !(event.target instanceof Element)) return;
        if (event.target.closest('[data-view-back]')) {
          event.preventDefault();
          this.close();
          return;
        }
        const insideContent = event.target.closest(
          '.product-view__image-stage, .product-view__summary, .product-view__rail-content, .product-view__specs, .product-view__mobile-purchase'
        );
        if (!insideContent) this.close();
      });
      this.productView?.addEventListener('keydown', this.onProductViewKeyDown);
      this.productView?.querySelector('[data-view-add]')?.addEventListener('click', (event) => {
        if (this.active) this.addToCart(this.active, event.currentTarget);
      });
      this.productView?.querySelector('[data-view-quantity-decrease]')?.addEventListener('click', () => this.adjustSelectedProductQuantity(-1));
      this.productView?.querySelector('[data-view-quantity-increase]')?.addEventListener('click', () => this.adjustSelectedProductQuantity(1));
      this.productView?.querySelector('[data-view-quantity-input]')?.addEventListener('change', () => this.syncProductQuantity());
      this.productView?.querySelector('[data-view-remove]')?.addEventListener('click', () => {
        if (this.active) this.removeFromCart(this.active);
      });
      this.productView?.querySelector('[data-view-mobile-add]')?.addEventListener('click', (event) => {
        if (this.active) this.addToCart(this.active, event.currentTarget);
      });
      this.productView?.querySelector('[data-view-mobile-quantity-decrease]')?.addEventListener('click', () => this.adjustSelectedProductQuantity(-1));
      this.productView?.querySelector('[data-view-mobile-quantity-increase]')?.addEventListener('click', () => this.adjustSelectedProductQuantity(1));
      this.productView?.querySelector('[data-view-mobile-quantity-input]')?.addEventListener('change', () => this.syncProductQuantity());
      this.productView?.querySelector('[data-view-mobile-remove]')?.addEventListener('click', () => {
        if (this.active) this.removeFromCart(this.active);
      });
      this.productView?.addEventListener('scroll', this.onProductViewScroll, { passive: true });
      this.productView?.addEventListener('click', (event) => {
        const variant = event.target instanceof Element ? event.target.closest('[data-variant-id]') : null;
        if (!variant || !this.active) return;
        const buttons = variant.closest('.product-card__sizes')?.querySelectorAll('[data-variant-id]') || [];
        buttons.forEach((button) => button.classList.toggle('is-selected', button === variant));
        this.active.element.dataset.variantId = variant.dataset.variantId || '';
        const price = this.productView.querySelector('[data-view-price]');
        if (price && variant.dataset.variantPrice) price.textContent = variant.dataset.variantPrice;
        this.productView.querySelectorAll('[data-view-quantity-input], [data-view-mobile-quantity-input]').forEach((input) => { input.value = '1'; });
        this.syncProductQuantity(this.active);
      });
      window.addEventListener('resize', this.onResize, { passive: true });
      window.addEventListener('popstate', this.onPopState);
      document.addEventListener('shopify:section:unload', (event) => {
        if (event.target.contains(this.root)) this.destroy();
      });
    }

    build() {
      cancelAnimationFrame(this.raf);
      this.close(false, true);
      this.field.replaceChildren();
      this.cards = [];
      this.isDesktop = window.matchMedia(DESKTOP_QUERY).matches;
      if (!this.sources.length) return;

      const rect = this.viewport.getBoundingClientRect();
      const styles = getComputedStyle(this.root);
      const configuredCell = parseFloat(styles.getPropertyValue('--cell-size')) || 220;
      this.cell = this.isDesktop ? configuredCell : clamp(rect.width * .44, 150, 190);
      this.rowStep = this.cell * 1.02;
      this.panSpeed = parseFloat(styles.getPropertyValue('--pan-speed')) || 3.2;
      this.cols = this.isDesktop
        ? Math.max(5, Math.ceil(rect.width / this.cell) + 4)
        : Math.max(5, Math.ceil(rect.width / this.cell) + 2);
      this.rows = this.isDesktop
        ? Math.max(6, Math.ceil(rect.height / this.rowStep) + 4)
        : Math.max(7, Math.ceil(rect.height / this.rowStep) + 2);
      this.worldW = this.cols * this.cell;
      this.worldH = this.rows * this.rowStep;

      for (let row = 0; row < this.rows; row += 1) {
        for (let col = 0; col < this.cols; col += 1) {
          const productIndex = mod(row * this.cols + col, this.sources.length);
          const element = this.sources[productIndex].cloneNode(true);
          const pattern = mod(row * 5 + col * 7, 6);
          const width = this.cell * [1.08, .82, .94, 1.06, .86, 1][pattern];
          const height = this.cell * 1.02;
          const rowOffset = [-100, -210, -82, -210, 105][row % 5];
          const baseX = col * this.cell + (this.cell - width) * .5 + rowOffset;
          const baseY = row * this.rowStep;
          this.mountCard(element, productIndex, { baseX, baseY, width, height, pattern });
        }
      }

      this.updateCounter(0);
      this.raf = requestAnimationFrame(this.tick);
    }

    mountCard(element, productIndex, layout) {
      element.style.setProperty('--sample-hue', String(mod(productIndex * 47 + 12, 320)));
      element.style.setProperty('--object-rotate', '0deg');
      const detail = element.querySelector('[data-product-detail]');
      const purchaseBar = element.querySelector('.product-card__purchase-bar');
      const model = {
        element,
        detail,
        purchaseBar,
        productIndex,
        layout,
        x: layout ? layout.baseX : 0,
        y: layout ? layout.baseY : 0,
        width: layout ? layout.width : 0,
        height: layout ? layout.height : 0,
        targetX: layout ? layout.baseX : 0,
        targetY: layout ? layout.baseY : 0,
        targetScale: 1,
        renderX: layout ? layout.baseX : 0,
        renderY: layout ? layout.baseY : 0,
        renderW: layout ? layout.width : 0,
        renderH: layout ? layout.height : 0,
        renderScale: 1
      };
      element.querySelector('[data-open-product]')?.addEventListener('click', (event) => {
        if (performance.now() < this.suppressClickUntil) {
          event.preventDefault();
          event.stopPropagation();
          return;
        }
        event.stopPropagation();
        if (!this.introComplete) return;
        if (this.active === model) this.close();
        else this.open(model);
      });
      element.querySelector('[data-close-product]')?.addEventListener('click', (event) => {
        event.stopPropagation();
        this.close();
      });
      element.querySelector('[data-add-to-cart]')?.addEventListener('click', (event) => {
        event.stopPropagation();
        this.addToCart(model);
      });
      this.field.appendChild(element);
      this.cards.push(model);
    }

    open(model, options = {}) {
      if (!this.introComplete) return;
      if (this.active || this.closing || !this.productView || !this.viewTrack || !this.viewStage) return;
      if (this.searchActive) this.dismissSearchChrome(false);
      this.active = model;
      this.settlingUntil = 0;
      model.sourceRect = model.element.querySelector('.product-card__media')?.getBoundingClientRect();
      this.populateProductView(model);
      this.root.querySelector('.infinite-shop__mobile-menu')?.removeAttribute('open');
      this.refreshCartState();
      this.root.classList.add('is-open');
      this.animateNavigation(true);
      this.animateProductView(model);
      this.updateCounter(model.productIndex);
      const title = model.element.dataset.title;
      if (title) document.title = `${title} — ${this.shopTitle}`;
      if (options.updateHistory !== false) this.updateProductRoute(model);
    }

    close(restoreFocus = true, immediate = false, updateHistory = true) {
      if (!this.active || this.closing) return;
      if (updateHistory && !immediate && this.productHandleFromLocation()) {
        if (this.routeWasPushed) {
          window.history.back();
          return;
        }
        this.restoreShopRoute();
      }
      const previous = this.active;
      this.closing = true;
      this.root.classList.add('is-closing');
      this.animateNavigation(false);
      if (immediate || this.reducedMotion || !window.gsap) {
        this.finishProductClose(previous, restoreFocus);
        return;
      }
      this.animateProductClose(previous, restoreFocus);
    }

    cartQuantityForVariant(variantId) {
      if (!this.cartState || !variantId) return 0;
      return this.cartState.items.reduce((quantity, item) => (
        String(item.variant_id) === String(variantId) ? quantity + Number(item.quantity || 0) : quantity
      ), 0);
    }

    syncGlobalCartCount() {
      if (!this.cartState) return;
      this.root.querySelectorAll('[data-cart-count]').forEach((count) => {
        count.textContent = String(this.cartState.item_count || 0);
      });
      this.root.querySelectorAll('[data-cart-target]').forEach((target) => {
        target.classList.toggle('has-items', Number(this.cartState.item_count || 0) > 0);
      });
    }

    syncProductQuantity(model = this.active) {
      if (!model || !this.productView) return;
      const variantId = model.element.dataset.variantId;
      const quantity = this.cartQuantityForVariant(variantId);
      const quantityControls = this.productView.querySelectorAll('[data-view-quantity-control], [data-view-mobile-quantity-control]');
      const addButtons = this.productView.querySelectorAll('[data-view-add], [data-view-mobile-add]');
      const quantityInputs = this.productView.querySelectorAll('[data-view-quantity-input], [data-view-mobile-quantity-input]');
      const decreaseButtons = this.productView.querySelectorAll('[data-view-quantity-decrease], [data-view-mobile-quantity-decrease]');
      const increaseButtons = this.productView.querySelectorAll('[data-view-quantity-increase], [data-view-mobile-quantity-increase]');
      const cartStates = this.productView.querySelectorAll('[data-view-cart-state]');
      const cartLinks = this.productView.querySelectorAll('[data-view-cart-link], [data-view-mobile-cart-link]');
      const removeButtons = this.productView.querySelectorAll('[data-view-remove], [data-view-mobile-remove]');
      const available = this.productView.dataset.productAvailable === 'true';
      const title = model.element.dataset.title || 'this item';
      const selectedQuantity = this.selectedProductQuantity();

      quantityInputs.forEach((input) => { input.value = String(selectedQuantity); });
      decreaseButtons.forEach((button) => { button.disabled = selectedQuantity <= 1; });
      increaseButtons.forEach((button) => { button.disabled = selectedQuantity >= 99; });
      cartStates.forEach((element) => { element.hidden = quantity < 1; element.textContent = quantity ? `${quantity} in cart` : ''; });
      cartLinks.forEach((link) => {
        link.hidden = quantity < 1;
        link.setAttribute('aria-label', `View cart with ${this.cartState?.item_count || quantity} items`);
      });
      removeButtons.forEach((button) => {
        button.hidden = quantity < 1;
        button.setAttribute('aria-label', `Remove ${title} from cart`);
      });
      quantityControls.forEach((control) => control.setAttribute('aria-label', `Quantity to add for ${title}: ${selectedQuantity}`));
      addButtons.forEach((button) => {
        button.disabled = !available;
        button.setAttribute('aria-disabled', String(!available));
        button.setAttribute('aria-label', `Add ${selectedQuantity} ${title} to cart`);
      });
    }

    selectedProductQuantity() {
      const input = this.productView?.querySelector('[data-view-quantity-input]');
      if (!input) return 1;
      const value = clamp(Math.trunc(Number(input.value)) || 1, 1, 99);
      input.value = String(value);
      return value;
    }

    adjustSelectedProductQuantity(delta) {
      const input = this.productView?.querySelector('[data-view-quantity-input]');
      if (!input) return;
      input.value = String(clamp((Math.trunc(Number(input.value)) || 1) + delta, 1, 99));
      this.syncProductQuantity();
    }

    setCartControlsBusy(busy) {
      this.productView?.querySelectorAll('[data-view-quantity-control], [data-view-mobile-quantity-control]').forEach((control) => {
        control.setAttribute('aria-busy', String(busy));
      });
      this.productView?.querySelectorAll('[data-view-add], [data-view-remove], [data-view-mobile-add], [data-view-mobile-remove], [data-view-quantity-decrease], [data-view-quantity-increase], [data-view-mobile-quantity-decrease], [data-view-mobile-quantity-increase], [data-view-quantity-input], [data-view-mobile-quantity-input]').forEach((button) => {
        button.disabled = busy;
      });
    }

    announceCart(message) {
      const status = this.productView?.querySelector('[data-card-status]');
      clearTimeout(this.cartStatusTimer);
      if (!status) return;
      status.textContent = message;
      this.cartStatusTimer = window.setTimeout(() => {
        if (status.isConnected) status.textContent = '';
      }, 3200);
    }

    applyPreviewCartChange(variantId, delta) {
      const items = this.cartState?.items || [];
      let line = items.find((item) => String(item.variant_id) === String(variantId));
      if (!line && delta > 0) {
        line = { key: `preview-${variantId}`, variant_id: Number(variantId), quantity: 0 };
        items.push(line);
      }
      if (line) line.quantity = Math.max(0, Number(line.quantity || 0) + delta);
      this.cartState = {
        item_count: items.reduce((total, item) => total + Number(item.quantity || 0), 0),
        items: items.filter((item) => item.quantity > 0)
      };
      return this.cartState;
    }

    async fetchCartState() {
      if (this.previewCart) return this.cartState;
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) throw new Error('Unable to read your cart');
      this.cartState = await response.json();
      return this.cartState;
    }

    async refreshCartState() {
      try {
        await this.fetchCartState();
        this.syncGlobalCartCount();
        this.syncProductQuantity();
      } catch (_) {
        this.announceCart('Cart quantity is temporarily unavailable.');
      }
    }

    async changeProductQuantity(model, delta, sourceButton = null) {
      const variantId = Number(model.element.dataset.variantId);
      if (!variantId || !delta || !this.productView) return;
      const currentQuantity = this.cartQuantityForVariant(variantId);
      if (delta < 0 && currentQuantity < 1) return;

      this.setCartControlsBusy(true);
        this.announceCart(delta > 0 ? `Adding ${delta}…` : 'Removing…');
      try {
        if (this.previewCart) {
          this.applyPreviewCartChange(variantId, delta);
        } else if (delta > 0) {
          const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [{ id: variantId, quantity: delta }] })
          });
          if (!response.ok) throw new Error('Unable to add this item');
          await this.fetchCartState();
        } else {
          const line = this.cartState?.items.find((item) => String(item.variant_id) === String(variantId));
          if (!line) return;
          const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: line.key, quantity: Math.max(0, Number(line.quantity || 0) + delta) })
          });
          if (!response.ok) throw new Error('Unable to remove this item');
          this.cartState = await response.json();
        }

        this.syncGlobalCartCount();
        this.syncProductQuantity(model);
        const nextQuantity = this.cartQuantityForVariant(variantId);
        this.announceCart(delta > 0
          ? `Added ${delta}. ${nextQuantity} in cart.`
          : nextQuantity > 0 ? `Removed ${Math.abs(delta)}. ${nextQuantity} in cart.` : 'Removed from cart.');
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      } catch (error) {
        this.announceCart(error.message || 'Unable to update your cart');
      } finally {
        this.setCartControlsBusy(false);
        this.syncProductQuantity(model);
      }
    }

    addToCart(model, sourceButton = null) {
      const quantity = this.selectedProductQuantity();
      this.animateCartBubble(sourceButton, quantity);
      return this.changeProductQuantity(model, quantity, sourceButton);
    }

    animateCartBubble(sourceButton, quantity) {
      if (!sourceButton || this.reducedMotion) return;
      const targets = [
        this.root.querySelector('[data-cart-target]'),
        this.root.querySelector('.infinite-shop__cart'),
        this.productView?.querySelector('[data-view-mobile-cart-link]:not([hidden])')
      ].filter(Boolean);
      const target = targets.find((candidate) => {
        const bounds = candidate.getBoundingClientRect();
        return bounds.width > 0 && bounds.height > 0;
      });
      if (!target) return;
      const sourceRect = sourceButton.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();
      if (!sourceRect.width || !sourceRect.height || !targetRect.width || !targetRect.height) return;
      const deltaX = targetRect.left + targetRect.width / 2 - (sourceRect.left + sourceRect.width / 2);
      const deltaY = targetRect.top + targetRect.height / 2 - (sourceRect.top + sourceRect.height / 2);

      const flyer = document.createElement('span');
      flyer.className = 'product-view__cart-flyer';
      flyer.textContent = `+${quantity}`;
      flyer.setAttribute('aria-hidden', 'true');
      flyer.style.left = `${sourceRect.left + sourceRect.width / 2 - 21}px`;
      flyer.style.top = `${sourceRect.top + sourceRect.height / 2 - 21}px`;
      document.body.appendChild(flyer);

      flyer.classList.add('is-flying');
      const arc = -Math.min(150, Math.max(50, Math.hypot(deltaX, deltaY) * .12));
      const controlX = deltaX * .45;
      const controlY = deltaY * .45 + arc;
      const startedAt = performance.now();
      const duration = 920;
      const easeOutCubic = (value) => 1 - Math.pow(1 - value, 3);
      const fly = (now) => {
        if (!flyer.isConnected) return;
        const progress = Math.min(1, (now - startedAt) / duration);
        const t = easeOutCubic(progress);
        const inverse = 1 - t;
        const x = (2 * inverse * t * controlX) + (t * t * deltaX);
        const y = (2 * inverse * t * controlY) + (t * t * deltaY);
        const scale = progress < .2 ? .58 + (progress / .2) * .62 : 1.2 - ((progress - .2) / .8) * .9;
        const opacity = progress < .12 ? progress / .12 : progress > .72 ? 1 - ((progress - .72) / .28) : 1;
        flyer.style.opacity = String(Math.max(0, opacity));
        flyer.style.transform = `translate3d(${x}px, ${y}px, 0) scale(${Math.max(.3, scale)})`;
        if (progress < 1) window.requestAnimationFrame(fly);
        else {
          flyer.remove();
          target.classList.remove('is-cart-targeting');
          void target.offsetWidth;
          target.classList.add('is-cart-targeting');
          window.setTimeout(() => target.classList.remove('is-cart-targeting'), 420);
        }
      };
      window.requestAnimationFrame(fly);
    }

    removeFromCart(model) {
      const quantity = this.cartQuantityForVariant(model.element.dataset.variantId);
      return this.changeProductQuantity(model, -quantity);
    }

    onPointerDown(event) {
      if (!this.introComplete || this.active || !event.isPrimary || event.button !== 0) return;
      this.drag = {
        id: event.pointerId,
        x: event.clientX,
        y: event.clientY,
        panX: this.pan.tx,
        panY: this.pan.ty,
        moved: false
      };
    }

    onPointerMove(event) {
      const rect = this.viewport.getBoundingClientRect();
      this.pointer.x = event.clientX - rect.left;
      this.pointer.y = event.clientY - rect.top;
      this.pointer.inside = true;
      if (!this.drag || this.drag.id !== event.pointerId) return;
      const deltaX = event.clientX - this.drag.x;
      const deltaY = event.clientY - this.drag.y;
      if (!this.drag.moved && Math.hypot(deltaX, deltaY) > 7) {
        this.drag.moved = true;
        this.root.classList.add('has-explored');
        this.root.classList.add('is-dragging');
        this.viewport.setPointerCapture(event.pointerId);
        if (this.viewport.contains(document.activeElement)) document.activeElement.blur();
      }
      this.pan.tx = this.drag.panX + deltaX;
      this.pan.ty = this.drag.panY + deltaY;
    }

    onPointerUp(event) {
      if (!this.drag || this.drag.id !== event.pointerId) return;
      if (this.drag.moved) this.suppressClickUntil = performance.now() + 260;
      this.drag = null;
      this.root.classList.remove('is-dragging');
      if (this.viewport.hasPointerCapture(event.pointerId)) this.viewport.releasePointerCapture(event.pointerId);
    }

    onWheel(event) {
      if (!this.introComplete || !this.isDesktop || this.active) return;
      event.preventDefault();
      this.pan.tx -= event.deltaX || event.deltaY * .22;
      this.pan.ty -= event.deltaY;
    }

    onGalleryWheel(event) {
      if (!this.active || this.closing || this.reducedMotion || event.ctrlKey || !this.viewStage) return;
      const maximum = this.galleryMaximum();
      if (!maximum) return;

      let delta = Math.abs(event.deltaY) >= Math.abs(event.deltaX) ? event.deltaY : event.deltaX;
      if (event.deltaMode === 1) delta *= 16;
      else if (event.deltaMode === 2) delta *= this.isDesktop ? this.viewStage.clientHeight : this.viewStage.clientWidth;

      event.preventDefault();
      clearTimeout(this.galleryWheel.resetTimer);
      this.galleryWheel.resetTimer = setTimeout(() => {
        this.galleryWheel.active = false;
      }, 150);
      const now = performance.now();
      if (this.galleryWheel.active || now < this.galleryWheel.lockedUntil || Math.abs(delta) < 1) return;

      const positions = this.galleryPagePositions();
      const currentPosition = this.galleryScroll.raf ? this.galleryScroll.target : this.galleryPosition();
      const currentPage = positions.reduce((nearest, position, index) => (
        Math.abs(position - currentPosition) < Math.abs(positions[nearest] - currentPosition) ? index : nearest
      ), 0);
      const nextPage = clamp(currentPage + (delta > 0 ? 1 : -1), 0, positions.length - 1);
      this.galleryWheel.active = true;
      this.galleryWheel.lockedUntil = now + 420;
      if (!this.galleryScroll.raf) this.galleryScroll.current = this.galleryPosition();
      this.galleryScroll.target = positions[nextPage];
      this.startGalleryGlide();
      this.queueGallerySettle();
    }

    onGalleryScroll() {
      this.updateGalleryCounter();
      if (
        !this.viewStage
        || this.galleryDrag?.moved
        || this.galleryScroll.driving
        || performance.now() < this.galleryScroll.ignoreNativeUntil
      ) return;
      this.galleryScroll.current = this.galleryPosition();
      this.galleryScroll.target = this.galleryPosition();
      this.queueGallerySettle();
    }

    onGalleryPointerDown(event) {
      if (this.isDesktop || !this.active || this.closing || !event.isPrimary || event.button !== 0 || !this.viewStage) return;
      this.stopGalleryGlide(this.galleryPosition());
      this.galleryDrag = {
        id: event.pointerId,
        x: event.clientX,
        y: event.clientY,
        position: this.galleryPosition(),
        moved: false
      };
    }

    onGalleryPointerMove(event) {
      if (!this.galleryDrag || this.galleryDrag.id !== event.pointerId || !this.viewStage) return;
      const deltaX = event.clientX - this.galleryDrag.x;
      const deltaY = event.clientY - this.galleryDrag.y;
      if (!this.galleryDrag.moved) {
        if (Math.abs(deltaX) < 7 && Math.abs(deltaY) < 7) return;
        if (Math.abs(deltaY) > Math.abs(deltaX)) {
          this.galleryDrag = null;
          return;
        }
        this.galleryDrag.moved = true;
        this.viewStage.classList.add('is-dragging');
        this.viewStage.setPointerCapture(event.pointerId);
      }
      event.preventDefault();
      const position = clamp(this.galleryDrag.position - deltaX, 0, this.galleryMaximum());
      this.setGalleryPosition(position);
      this.galleryScroll.current = position;
      this.galleryScroll.target = position;
      this.updateGalleryCounter();
    }

    onGalleryPointerUp(event) {
      if (!this.galleryDrag || this.galleryDrag.id !== event.pointerId || !this.viewStage) return;
      const moved = this.galleryDrag.moved;
      this.galleryDrag = null;
      this.viewStage.classList.remove('is-dragging');
      if (this.viewStage.hasPointerCapture(event.pointerId)) this.viewStage.releasePointerCapture(event.pointerId);
      if (moved) {
        if (!this.reducedMotion) this.viewStage.classList.add('is-gliding');
        this.queueGallerySettle();
      }
    }

    queueGallerySettle() {
      clearTimeout(this.galleryScroll.settleTimer);
      if (this.reducedMotion) return;
      this.galleryScroll.settleTimer = setTimeout(() => this.settleGallery(), 170);
    }

    settleGallery() {
      if (!this.active || this.closing || !this.viewStage || !this.galleryStep) return;
      const positions = this.galleryPagePositions();
      const nearest = positions.reduce((closest, position) => (
        Math.abs(position - this.galleryScroll.target) < Math.abs(closest - this.galleryScroll.target)
          ? position
          : closest
      ), positions[0]);
      this.galleryScroll.target = nearest;
      this.startGalleryGlide();
    }

    startGalleryGlide() {
      if (this.galleryScroll.raf || !this.viewStage) return;
      this.viewStage.classList.add('is-gliding');
      this.galleryScroll.previousTime = performance.now();
      const glide = (time) => {
        if (!this.viewStage || !this.active || this.closing) {
          this.stopGalleryGlide();
          return;
        }
        const elapsed = Math.min(34, Math.max(1, time - this.galleryScroll.previousTime));
        this.galleryScroll.previousTime = time;
        const maximum = this.galleryMaximum();
        this.galleryScroll.target = clamp(this.galleryScroll.target, 0, maximum);
        const distance = this.galleryScroll.target - this.galleryScroll.current;
        const smoothing = 1 - Math.exp(-elapsed / 118);
        this.galleryScroll.current += distance * smoothing;
        this.galleryScroll.driving = true;
        this.galleryScroll.ignoreNativeUntil = performance.now() + 50;
        this.setGalleryPosition(this.galleryScroll.current);
        this.galleryScroll.driving = false;
        this.updateGalleryCounter();

        if (Math.abs(distance) < .2) {
          this.galleryScroll.current = this.galleryScroll.target;
          this.galleryScroll.driving = true;
          this.galleryScroll.ignoreNativeUntil = performance.now() + 50;
          this.setGalleryPosition(this.galleryScroll.target);
          this.galleryScroll.driving = false;
          this.galleryScroll.raf = 0;
          this.viewStage.classList.remove('is-gliding');
          return;
        }
        this.galleryScroll.raf = requestAnimationFrame(glide);
      };
      this.galleryScroll.raf = requestAnimationFrame(glide);
    }

    galleryPosition() {
      if (!this.viewStage) return 0;
      return this.isDesktop ? this.viewStage.scrollTop : this.viewStage.scrollLeft;
    }

    galleryMaximum() {
      if (!this.viewStage) return 0;
      return this.isDesktop
        ? Math.max(0, this.viewStage.scrollHeight - this.viewStage.clientHeight)
        : Math.max(0, this.viewStage.scrollWidth - this.viewStage.clientWidth);
    }

    galleryPagePositions() {
      if (!this.viewStage) return [0];
      const total = this.viewTrack?.querySelectorAll('[data-gallery-slide]').length || 1;
      const maximum = this.galleryMaximum();
      const step = Math.max(1, this.galleryStep);
      return [...new Set(Array.from({ length: total }, (_, index) => Math.min(index * step, maximum)))];
    }

    setGalleryPosition(position) {
      if (!this.viewStage) return;
      if (this.isDesktop) this.viewStage.scrollTop = position;
      else this.viewStage.scrollLeft = position;
    }

    stopGalleryGlide(position = this.galleryPosition()) {
      cancelAnimationFrame(this.galleryScroll.raf);
      clearTimeout(this.galleryScroll.settleTimer);
      this.galleryScroll.raf = 0;
      this.galleryScroll.settleTimer = 0;
      this.galleryScroll.current = position;
      this.galleryScroll.target = position;
      this.galleryScroll.previousTime = 0;
      this.galleryScroll.ignoreNativeUntil = performance.now() + 50;
      this.galleryScroll.driving = false;
      this.viewStage?.classList.remove('is-gliding');
      clearTimeout(this.galleryWheel.resetTimer);
      this.galleryWheel.active = false;
      this.galleryWheel.lockedUntil = 0;
    }

    updateGalleryCounter() {
      if (!this.viewGalleryCurrent || !this.viewStage) return;
      const total = this.viewTrack?.querySelectorAll('[data-gallery-slide]').length || 1;
      const current = Math.min(total, Math.max(1, Math.round(this.galleryPosition() / Math.max(1, this.galleryStep)) + 1));
      this.viewGalleryCurrent.textContent = String(current).padStart(2, '0');
    }

    onKeyDown(event) {
      if (event.key === 'Escape' && this.active) {
        event.preventDefault();
        this.close();
        return;
      }
      if (!this.introComplete || !this.isDesktop || this.active) return;
      const movement = event.shiftKey ? 220 : 80;
      const deltas = { ArrowLeft: [movement, 0], ArrowRight: [-movement, 0], ArrowUp: [0, movement], ArrowDown: [0, -movement] };
      if (!deltas[event.key]) return;
      event.preventDefault();
      this.pan.tx += deltas[event.key][0];
      this.pan.ty += deltas[event.key][1];
    }

    setProductDialogState(open) {
      [this.viewport, this.root.querySelector('.infinite-shop__nav')]
        .filter(Boolean)
        .forEach((element) => {
          element.inert = open;
          if (open) element.setAttribute('inert', '');
          else element.removeAttribute('inert');
        });

      if (open) {
        const focusEntry = () => {
          if (!this.active || this.closing) return;
          const entry = this.productView.querySelector('[data-view-back]') || this.productView;
          entry.focus();
          if (document.activeElement !== entry) this.productView.focus();
        };
        focusEntry();
        requestAnimationFrame(() => {
          focusEntry();
        });
        clearTimeout(this.dialogFocusTimer);
        this.dialogFocusTimer = setTimeout(focusEntry, 100);
      } else {
        clearTimeout(this.dialogFocusTimer);
      }
    }

    onProductViewKeyDown(event) {
      if (!this.active) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;

      const focusable = [...this.productView.querySelectorAll(
        'a[href], button:not([disabled]), summary, input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )].filter((element) => element.getAttribute('aria-hidden') !== 'true');
      if (!focusable.length) {
        event.preventDefault();
        this.productView.focus({ preventScroll: true });
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!focusable.includes(document.activeElement)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus({ preventScroll: true });
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus({ preventScroll: true });
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus({ preventScroll: true });
      }
    }

    onProductViewScroll() {
      if (this.mobilePurchaseRaf) return;
      this.mobilePurchaseRaf = requestAnimationFrame(() => {
        this.mobilePurchaseRaf = 0;
        this.updateMobilePurchaseVisibility();
      });
    }

    updateMobilePurchaseVisibility() {
      if (!this.mobilePurchase || !this.productView || !this.viewRail) return;
      const mobileHeaderHeight = 68;
      const visible = Boolean(
        this.active
        && !this.isDesktop
        && this.productView.scrollTop > this.viewRail.offsetTop - mobileHeaderHeight
      );
      this.viewRail.classList.toggle('is-passed', visible);
      this.mobilePurchase.classList.toggle('is-visible', visible);
      this.mobilePurchase.setAttribute('aria-hidden', String(!visible));
      this.mobilePurchase.inert = !visible;
      this.mobilePurchase.toggleAttribute('inert', !visible);
    }

    onResize() {
      if (!this.introComplete) return;
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => {
        this.isDesktop = window.matchMedia(DESKTOP_QUERY).matches;
        if (this.active) {
          this.layoutGallery(false);
          this.updateMobilePurchaseVisibility();
          return;
        }
        this.build();
      }, 180);
    }

    runIntro() {
      const gsap = window.gsap;
      const wordmark = this.root.querySelector('.infinite-shop__wordmark');
      const logoGraphic = wordmark?.querySelector('.infinite-shop__logo-lockup');
      const logoSymbol = logoGraphic?.querySelector('[data-logo-symbol]');
      const logoWhy = logoGraphic?.querySelector('[data-logo-why]');
      const logoApparel = logoGraphic?.querySelector('[data-logo-apparel]');
      const logoParts = [logoSymbol, logoWhy, logoApparel].filter(Boolean);
      const utilities = this.root.querySelector('.infinite-shop__utilities');
      const dragHint = this.dragHint;
      const media = this.cards.map((card) => card.element.querySelector('.product-card__media')).filter(Boolean);

      const finish = () => {
        this.introComplete = true;
        this.root.classList.remove('is-intro');
        this.root.style.removeProperty('--bar-width');
        if (gsap) gsap.set([wordmark, logoGraphic, ...logoParts, utilities, dragHint, ...media].filter(Boolean), { clearProps: 'all' });
      };

      if (!gsap || this.reducedMotion || !wordmark || !utilities) {
        finish();
        return;
      }

      const viewportRect = this.viewport.getBoundingClientRect();
      const width = viewportRect.width || window.innerWidth;
      const height = viewportRect.height || window.innerHeight;
      const edge = this.isDesktop ? clamp(width * .014, 14, 24) : 10;
      const padding = this.isDesktop ? clamp(width * .017, 16, 29) : 10;
      const logoTarget = this.isDesktop ? edge + padding : 20;
      const railWidth = Math.max(1, width - edge * 2);
      const viewBoxWidth = logoGraphic?.viewBox?.baseVal?.width || 3745.62;
      const renderedLogoWidth = logoGraphic?.getBoundingClientRect().width || (this.isDesktop ? 210 : 142);
      const logoScale = renderedLogoWidth / viewBoxWidth;
      const stagePadding = this.isDesktop ? 26 : 18;
      const unionBounds = (elements, fallback) => {
        try {
          const boxes = elements.map((element) => element?.getBBox()).filter((box) => box?.width > 0);
          if (boxes.length !== elements.length) return fallback;
          const left = Math.min(...boxes.map((box) => box.x));
          const right = Math.max(...boxes.map((box) => box.x + box.width));
          return { x: left, width: right - left };
        } catch (_) {
          return fallback;
        }
      };
      const stageFrom = (bounds) => ({
        x: (viewBoxWidth / 2 - (bounds.x + bounds.width / 2)) * logoScale,
        barWidth: bounds.width * logoScale + stagePadding
      });
      const symbolStage = stageFrom(unionBounds([logoSymbol], { x: 0, width: 735 }));
      const whyStage = stageFrom(unionBounds([logoSymbol, logoWhy], { x: 0, width: 2177 }));
      const fullStage = stageFrom(unionBounds(logoParts, { x: 0, width: viewBoxWidth }));
      const viewportCenter = { x: viewportRect.left + width / 2, y: viewportRect.top + height / 2 };
      const maximumWaveDistance = Math.max(1, Math.hypot(width / 2, height / 2));
      const productWaveDelay = (index, element) => {
        const card = this.cards[index];
        const bounds = element.getBoundingClientRect();
        const cardCenter = card?.layout ? {
          x: viewportRect.left
            + mod(card.layout.baseX + this.pan.x + this.cell, this.worldW)
            - this.cell
            + card.layout.width / 2,
          y: viewportRect.top
            + mod(card.layout.baseY + this.pan.y + this.rowStep, this.worldH)
            - this.rowStep
            + card.layout.height / 2
        } : {
          x: bounds.left + bounds.width / 2,
          y: bounds.top + bounds.height / 2
        };
        const distance = Math.hypot(
          cardCenter.x - viewportCenter.x,
          cardCenter.y - viewportCenter.y
        );
        return clamp(distance / maximumWaveDistance, 0, 1) * .62;
      };

      gsap.set(wordmark, { left: '50%', top: '50%', xPercent: -50, yPercent: -50, autoAlpha: 1 });
      gsap.set(logoGraphic, { autoAlpha: 1, x: symbolStage.x });
      gsap.set(logoSymbol, { autoAlpha: 1 });
      gsap.set([logoWhy, logoApparel], { autoAlpha: 0 });
      gsap.set(this.root, { '--bar-width': `${symbolStage.barWidth}px` });
      gsap.set(utilities, { autoAlpha: 0, y: 0 });
      if (dragHint) gsap.set(dragHint, { autoAlpha: 0, y: 6 });
      gsap.set(media, { autoAlpha: 1, y: 0, scale: 0, transformOrigin: 'center center' });

      this.introTimeline = gsap.timeline({ defaults: { ease: 'none' }, onComplete: finish });
      this.introTimeline
        .to(this.root, { duration: .48 })
        .set(logoWhy, { autoAlpha: 1 })
        .set(logoGraphic, { x: whyStage.x })
        .set(this.root, { '--bar-width': `${whyStage.barWidth}px` })
        .to(this.root, { duration: .5 })
        .set(logoApparel, { autoAlpha: 1 })
        .set(logoGraphic, { x: fullStage.x })
        .set(this.root, { '--bar-width': `${fullStage.barWidth}px` })
        .to({}, { duration: .58 })
        .addLabel('railExpand')
        .to(this.root, {
          '--bar-width': `${railWidth}px`,
          duration: .86,
          ease: 'circ.inOut'
        }, 'railExpand')
        .to(wordmark, {
          left: logoTarget,
          xPercent: 0,
          duration: .86,
          ease: 'circ.inOut'
        }, 'railExpand')
        .to(utilities, { autoAlpha: 1, duration: .26, ease: 'power2.out' }, 'railExpand+=.54');

      media.forEach((element, index) => {
        const delay = .34 + productWaveDelay(index, element);
        this.introTimeline.to(element, {
          scale: 1,
          duration: .68,
          ease: 'power3.out'
        }, `railExpand+=${delay}`);
      });

      if (!this.isDesktop) {
        this.introTimeline
          .to(this.pan, { tx: -22, ty: -10, duration: .72, ease: 'power2.inOut' }, 'railExpand+=.7')
          .to(dragHint, { autoAlpha: 1, y: 0, duration: .3, ease: 'power2.out' }, 'railExpand+=1.18');
      }
    }

    animateNavigation(toTop) {
      const wordmark = this.root.querySelector('.infinite-shop__wordmark');
      const utilities = this.root.querySelector('.infinite-shop__utilities');
      const targets = [wordmark, utilities].filter(Boolean);
      const gsap = window.gsap;

      this.navTimeline?.kill();
      if (!gsap || this.reducedMotion || !targets.length) {
        this.root.classList.toggle('is-nav-top', toTop);
        return;
      }

      this.navTimeline = gsap.timeline();
      this.navTimeline
        .to(targets, { autoAlpha: 0, duration: .18, ease: 'power2.out' })
        .call(() => this.root.classList.toggle('is-nav-top', toTop))
        .to(targets, { autoAlpha: 1, duration: .24, ease: 'power2.inOut', clearProps: 'opacity,visibility' });
    }

    populateProductView(model) {
      const sourceImage = model.element.querySelector('.product-card__media img');
      const title = model.element.dataset.title || model.detail?.querySelector('h2')?.textContent?.trim() || '';
      const description = model.detail?.querySelector('.product-card__description')?.textContent?.trim() || '';
      const price = model.purchaseBar?.querySelector('strong')?.textContent?.trim() || '';
      const sourceAction = model.purchaseBar?.querySelector('button, .product-card__sold');
      const productAvailable = !sourceAction?.matches('[data-sold-out]');
      const specs = this.productView.querySelector('[data-view-specs]');
      const featured = sourceImage?.currentSrc || sourceImage?.src || '';
      const gallerySources = [...model.element.querySelectorAll('[data-product-gallery] [data-gallery-src]')]
        .map((item) => ({ src: item.dataset.gallerySrc, alt: item.dataset.galleryAlt || title }))
        .filter((item) => item.src);
      const imageKey = (src) => {
        try {
          const url = new URL(src, document.baseURI);
          return `${url.origin}${url.pathname}`;
        } catch {
          return src;
        }
      };
      const clickedImage = featured ? [{ src: featured, alt: sourceImage?.alt || title }] : [];
      const gallery = [
        ...clickedImage,
        ...gallerySources.filter((item) => !featured || imageKey(item.src) !== imageKey(featured))
      ].slice(0, 5);
      this.productView.dataset.productTitle = title;
      this.productView.dataset.productAvailable = String(productAvailable);
      this.productView.querySelectorAll('[data-view-quantity-input], [data-view-mobile-quantity-input]').forEach((input) => { input.value = '1'; });

      model.homePlaceholder?.remove();
      const homePlaceholder = sourceImage.cloneNode(false);
      homePlaceholder.removeAttribute('data-view-image');
      homePlaceholder.classList.add('product-card__image-placeholder');
      homePlaceholder.alt = '';
      homePlaceholder.setAttribute('aria-hidden', 'true');
      sourceImage.parentElement.appendChild(homePlaceholder);
      model.sourceImage = sourceImage;
      model.imageHome = sourceImage.parentElement;
      model.homePlaceholder = homePlaceholder;

      this.viewTrack.replaceChildren(...gallery.map((item, index) => {
        const slide = document.createElement('span');
        slide.className = 'product-view__gallery-slide';
        slide.dataset.gallerySlide = '';
        const image = document.createElement('img');
        image.src = item.src;
        image.alt = item.alt;
        image.loading = 'eager';
        image.decoding = 'async';
        if (index === 0) {
          image.classList.add('product-view__image-placeholder');
          image.alt = '';
          image.setAttribute('aria-hidden', 'true');
          model.viewPlaceholder = image;
        }
        slide.appendChild(image);
        return slide;
      }));
      this.viewImage = sourceImage;
      this.galleryStep = 0;
      this.stopGalleryGlide(0);
      this.viewStage.scrollTop = 0;
      this.viewStage.scrollLeft = 0;
      if (this.viewGalleryMeta) this.viewGalleryMeta.hidden = gallery.length < 2;
      if (this.viewGalleryCurrent) this.viewGalleryCurrent.textContent = '01';
      if (this.viewGalleryTotal) this.viewGalleryTotal.textContent = String(Math.max(1, gallery.length)).padStart(2, '0');
      this.productView.querySelector('[data-view-breadcrumb]').textContent = title;
      this.productView.querySelector('[data-view-title]').textContent = title;
      this.productView.querySelector('[data-view-description]').textContent = description;
      this.productView.querySelectorAll('[data-view-price], [data-view-mobile-price]').forEach((element) => {
        element.textContent = price;
      });
      const addButton = this.productView.querySelector('[data-view-add]');
      if (addButton) {
        addButton.disabled = !productAvailable;
        addButton.setAttribute('aria-disabled', String(!productAvailable));
      }

      specs.replaceChildren();
      ['.product-card__sizes', '.product-card__accordions'].forEach((selector) => {
        const source = model.detail?.querySelector(selector);
        if (source) specs.appendChild(source.cloneNode(true));
      });
      this.enrichProductSpecs(model, specs);
      const status = document.createElement('p');
      status.className = 'product-card__status';
      status.dataset.cardStatus = '';
      status.setAttribute('role', 'status');
      status.setAttribute('aria-live', 'polite');
      specs.appendChild(status);
      this.productView.scrollTop = 0;
      this.updateMobilePurchaseVisibility();
      this.syncProductQuantity(model);
    }

    enrichProductSpecs(model, specs) {
      const sizes = specs.querySelector('.product-card__sizes');
      const unavailableOptions = [...(sizes?.querySelectorAll('.is-unavailable') || [])];
      unavailableOptions.forEach((option) => {
        const label = option.textContent?.trim() || 'Option';
        option.setAttribute('aria-label', `${label}, sold out`);
      });
      if (sizes && unavailableOptions.length && !sizes.querySelector('.product-card__stock-note')) {
        const note = document.createElement('p');
        note.className = 'product-card__stock-note';
        const copy = document.createElement('span');
        copy.textContent = 'Sold-out options are crossed out.';
        const link = document.createElement('a');
        link.textContent = 'Ask about restock';
        link.href = `/pages/contact?product=${encodeURIComponent(this.modelHandle(model))}`;
        note.append(copy, link);
        sizes.appendChild(note);
      }

      const proof = model.detail?.querySelector('[data-product-proof]');
      if (proof) specs.appendChild(proof.cloneNode(true));

      const currentHandle = this.modelHandle(model);
      const relatedSources = this.allSources.filter((source) => this.modelHandle({ element: source }) !== currentHandle).slice(0, 3);
      if (!relatedSources.length) return;
      const section = document.createElement('section');
      section.className = 'product-view__related';
      const heading = document.createElement('h3');
      heading.id = `${this.productView.getAttribute('aria-labelledby') || 'ProductViewTitle'}-Related`;
      section.setAttribute('aria-labelledby', heading.id);
      heading.textContent = 'More from Why Wear';
      const list = document.createElement('div');
      list.className = 'product-view__related-list';
      relatedSources.forEach((source) => {
        const title = source.dataset.title || 'View product';
        const link = document.createElement('a');
        link.className = 'product-view__related-item';
        link.href = source.dataset.url || this.shopUrl;
        link.setAttribute('aria-label', `View ${title}`);
        const sourceImage = source.querySelector('.product-card__media img');
        if (sourceImage) {
          const image = sourceImage.cloneNode(false);
          image.alt = '';
          image.loading = 'lazy';
          link.appendChild(image);
        }
        const meta = document.createElement('span');
        const name = document.createElement('span');
        const price = document.createElement('strong');
        name.textContent = title;
        price.textContent = source.querySelector('.product-card__purchase-bar strong')?.textContent?.trim() || '';
        meta.append(name, price);
        link.appendChild(meta);
        list.appendChild(link);
      });
      section.append(heading, list);
      specs.appendChild(section);
    }

    moveProductImage(model, destination, options = {}) {
      const image = model.sourceImage;
      const gsap = window.gsap;
      const Flip = window.Flip;
      if (!image || !destination) return null;

      this.imageFlip?.kill();
      if (Flip) Flip.killFlipsOf(image, true);

      const finish = () => {
        if (!destination.isConnected) return;
        destination.replaceWith(image);
        image.classList.remove('product-view__transition-image');
        if (gsap) gsap.set(image, { clearProps: 'all' });
        options.onComplete?.();
      };

      if (!gsap || !Flip || this.reducedMotion) {
        options.beforeMove?.();
        finish();
        return null;
      }

      const state = Flip.getState(image);
      options.beforeMove?.();
      this.flipLayer.appendChild(image);
      image.classList.add('product-view__transition-image');
      Flip.fit(image, destination, { scale: false });
      this.imageFlip = Flip.from(state, {
        targets: image,
        absolute: true,
        scale: false,
        duration: options.duration || .82,
        ease: options.ease || 'power4.inOut',
        zIndex: 95,
        onComplete: finish
      });
      return this.imageFlip;
    }

    restoreProductImage(model) {
      const image = model?.sourceImage;
      if (!image || !model.imageHome) return;
      this.imageFlip?.kill();
      if (window.Flip) window.Flip.killFlipsOf(image, true);
      if (model.homePlaceholder?.isConnected) model.homePlaceholder.replaceWith(image);
      else if (image.parentElement !== model.imageHome) model.imageHome.appendChild(image);
      image.classList.remove('product-view__transition-image');
      image.removeAttribute('data-view-image');
      if (window.gsap) window.gsap.set(image, { clearProps: 'all' });
      model.homePlaceholder = null;
      model.viewPlaceholder = null;
      this.imageFlip = null;
    }

    layoutGallery(reset = false) {
      if (!this.viewStage || !this.viewTrack) return;
      const bounds = this.viewStage.getBoundingClientRect();
      const dimension = this.isDesktop ? bounds.height : bounds.width;
      if (!dimension) return;
      const previousStep = this.galleryStep || 1;
      const scrollProgress = reset ? 0 : this.galleryPosition() / previousStep;
      const peek = this.isDesktop ? clamp(dimension * .15, 96, 124) : clamp(dimension * .09, 28, 36);
      this.galleryStep = Math.max(1, dimension - peek);
      [...this.viewTrack.querySelectorAll('[data-gallery-slide]')].forEach((slide) => {
        slide.style.removeProperty('top');
        if (this.isDesktop) {
          slide.style.removeProperty('width');
          slide.style.height = `${this.galleryStep}px`;
        } else {
          slide.style.removeProperty('height');
          slide.style.width = `${this.galleryStep}px`;
        }
      });
      this.viewTrack.style.removeProperty('transform');
      this.setGalleryPosition(scrollProgress * this.galleryStep);
      this.stopGalleryGlide(this.galleryPosition());
      if (this.viewGalleryHint) this.viewGalleryHint.textContent = this.isDesktop ? 'Scroll for more' : 'Swipe for more';
      this.updateGalleryCounter();
    }

    productViewTargets() {
      return [...this.productView.querySelectorAll(
        '.product-view__mobile-brand, .product-view__close, .product-view__breadcrumb, .product-view__summary h2, .product-view__description, .product-view__rail-content > *, .product-view__specs > *'
      )];
    }

    animateProductView(model) {
      const gsap = window.gsap;
      const galleryImages = [...this.viewTrack.querySelectorAll('img')];
      const upcomingImages = galleryImages.slice(1);
      upcomingImages.forEach((image) => image.decode?.().catch(() => {}));
      if (this.active !== model) return;

      const targets = this.productViewTargets();
      this.productView.classList.add('is-visible');
      this.productView.setAttribute('aria-hidden', 'false');
      this.setProductDialogState(true);
      requestAnimationFrame(() => this.updateMobilePurchaseVisibility());
      this.transitionReady = true;
      this.layoutGallery(true);

      const imageAnimation = this.moveProductImage(model, model.viewPlaceholder, {
        duration: .82,
        ease: 'power4.inOut',
        beforeMove: () => model.element.classList.add('is-source'),
        onComplete: () => {
          model.sourceImage.dataset.viewImage = '';
          model.viewPlaceholder = null;
          this.viewImage = model.sourceImage;
          this.imageFlip = null;
        }
      });

      if (!gsap || this.reducedMotion) {
        this.pushProgress = 1;
        this.productView.style.setProperty('--view-bg-opacity', '1');
        targets.forEach((element) => { element.style.opacity = '1'; });
        upcomingImages.forEach((image) => { image.style.opacity = '1'; });
        return;
      }

      this.contentTimeline?.kill();
      gsap.set(this.productView, { '--view-bg-opacity': 0 });
      gsap.set(targets, { autoAlpha: 0, y: 12 });
      gsap.set(upcomingImages, { autoAlpha: 0 });

      this.contentTimeline = gsap.timeline();
      if (imageAnimation) this.contentTimeline.add(imageAnimation, 0);
      this.contentTimeline
        .to(this, { pushProgress: 1, duration: .82, ease: 'power4.inOut' }, 0)
        .to(this.productView, { '--view-bg-opacity': 1, duration: .12, ease: 'none' }, .7)
        .to(targets, { autoAlpha: 1, y: 0, duration: .32, stagger: .035, ease: 'power3.out' }, .58)
        // Hold the upcoming gallery frames until the infinite field has fully
        // cleared, then let the next angle appear as its own quiet beat.
        .to(upcomingImages, {
          autoAlpha: 1,
          duration: .34,
          stagger: .07,
          ease: 'power2.out'
        }, .92);
    }

    animateProductClose(model, restoreFocus) {
      const gsap = window.gsap;
      this.stopGalleryGlide();
      const targets = this.productViewTargets();
      const upcomingImages = [...this.viewTrack.querySelectorAll('img')].slice(1);

      this.contentTimeline?.kill();
      const imageAnimation = this.moveProductImage(model, model.homePlaceholder, {
        duration: .68,
        ease: 'power4.inOut',
        onComplete: () => {
          model.sourceImage.removeAttribute('data-view-image');
          model.homePlaceholder = null;
          this.imageFlip = null;
        }
      });
      this.contentTimeline = gsap.timeline({ onComplete: () => this.finishProductClose(model, restoreFocus) });
      if (imageAnimation) this.contentTimeline.add(imageAnimation, 0);
      this.contentTimeline
        .to(targets, { autoAlpha: 0, y: -8, duration: .18, ease: 'power2.in' }, 0)
        .to(upcomingImages, { autoAlpha: 0, duration: .12, ease: 'none' }, 0)
        .to(this.productView, { '--view-bg-opacity': 0, duration: .12, ease: 'none' }, 0)
        .to(this.viewStage, { scrollTop: 0, duration: .48, ease: 'power3.inOut' }, 0)
        .to(this, { pushProgress: 0, duration: .68, ease: 'power4.inOut' }, 0);
    }

    finishProductClose(model, restoreFocus) {
      const gsap = window.gsap;
      const targets = this.productViewTargets();
      const upcomingImages = [...this.viewTrack.querySelectorAll('img')].slice(1);
      this.contentTimeline?.kill();
      this.contentTimeline = null;
      this.restoreProductImage(model);
      if (gsap) gsap.set([this.viewStage, this.viewTrack, this.viewImage, ...upcomingImages, ...targets], { clearProps: 'all' });
      this.viewStage.scrollTop = 0;
      this.productView.style.removeProperty('--view-bg-opacity');
      this.productView.classList.remove('is-visible');
      this.productView.setAttribute('aria-hidden', 'true');
      this.productView.scrollTop = 0;
      this.viewRail?.classList.remove('is-passed');
      this.mobilePurchase?.classList.remove('is-visible');
      this.mobilePurchase?.setAttribute('aria-hidden', 'true');
      if (this.mobilePurchase) {
        this.mobilePurchase.inert = true;
        this.mobilePurchase.setAttribute('inert', '');
      }
      this.setProductDialogState(false);
      model.element.classList.remove('is-source');
      this.active = null;
      this.pointer.inside = false;
      this.closing = false;
      this.transitionReady = false;
      this.pushProgress = 0;
      this.galleryStep = 0;
      this.stopGalleryGlide(0);
      this.root.classList.remove('is-open');
      this.root.classList.remove('is-closing');
      if (this.isDesktop) this.settlingUntil = Date.now() + 700;
      const restoreSearchResults = Boolean(this.searchQuery);
      const reopenSearch = this.openSearchAfterClose;
      this.openSearchAfterClose = false;
      if (restoreSearchResults) this.resetSearchProducts();
      if (reopenSearch) requestAnimationFrame(() => this.openSearch());
      else if (restoreFocus && !restoreSearchResults) model.element.querySelector('[data-open-product]')?.focus({ preventScroll: true });
    }

    updateCounter(index) {
      if (!this.counter) return;
      const current = String(index + 1).padStart(2, '0');
      const total = String(this.sources.length).padStart(2, '0');
      this.counter.textContent = `${current} / ${total}`;
    }

    pushedPosition(card, x, y, width, height, expanded) {
      const pad = 36;
      const left = x - pad;
      const right = x + width + pad;
      const top = y - pad;
      const bottom = y + height + pad;
      const overlapX = Math.min(right, expanded.right) - Math.max(left, expanded.left);
      const overlapY = Math.min(bottom, expanded.bottom) - Math.max(top, expanded.top);
      if (overlapX <= 0 || overlapY <= 0) return { x, y };

      const exits = [
        { x: expanded.left - width - pad, y },
        { x: expanded.right + pad, y },
        { x, y: expanded.top - height - pad },
        { x, y: expanded.bottom + pad }
      ];
      return exits.reduce((nearest, candidate) => {
        const distance = Math.hypot(candidate.x - x, candidate.y - y);
        return distance < nearest.distance ? { ...candidate, distance } : nearest;
      }, { x, y, distance: Infinity });
    }

    tick() {
      const rect = this.viewport.getBoundingClientRect();
      const ease = this.reducedMotion ? 1 : .115;

      // Treat the product wall as one panning surface. The pointer sets a
      // direction and speed relative to the viewport centre; individual
      // products never react to or avoid the cursor.
      if (this.introComplete && this.pointer.inside && !this.drag && !this.active && !this.reducedMotion) {
        const deadZone = .1;
        const directionalSpeed = (value) => {
          const amount = Math.max(0, Math.abs(value) - deadZone) / (1 - deadZone);
          return -Math.sign(value) * Math.pow(amount, 1.45) * this.panSpeed;
        };
        const nx = clamp((this.pointer.x - rect.width / 2) / (rect.width / 2), -1, 1);
        const ny = clamp((this.pointer.y - rect.height / 2) / (rect.height / 2), -1, 1);
        this.pan.tx += directionalSpeed(nx);
        this.pan.ty += directionalSpeed(ny);
      }

      this.pan.x += (this.pan.tx - this.pan.x) * ease;
      this.pan.y += (this.pan.ty - this.pan.y) * ease;
      this.root.style.setProperty('--grid-x', `${mod(this.pan.x, this.cell)}px`);
      this.root.style.setProperty('--grid-y', `${mod(this.pan.y, this.cell)}px`);

      const wave = clamp(this.pushProgress, 0, 1);
      const movingImageRect = this.active?.sourceImage?.getBoundingClientRect() || this.active?.sourceRect;
      const imageBounds = movingImageRect ? {
        left: movingImageRect.left - rect.left,
        top: movingImageRect.top - rect.top,
        right: movingImageRect.right - rect.left,
        bottom: movingImageRect.bottom - rect.top
      } : { left: rect.width / 2, top: rect.height / 2, right: rect.width / 2, bottom: rect.height / 2 };
      const expanded = {
        left: imageBounds.left * (1 - wave),
        top: imageBounds.top * (1 - wave),
        right: imageBounds.right + (rect.width - imageBounds.right) * wave,
        bottom: imageBounds.bottom + (rect.height - imageBounds.bottom) * wave
      };
      // Keep the distance hierarchy anchored to where the product was picked.
      // This makes the scale wave spatially stable while the selected image is
      // itself moving into the detail layout.
      const scaleOriginRect = this.active?.sourceRect || movingImageRect;
      const scaleOriginX = scaleOriginRect
        ? scaleOriginRect.left - rect.left + scaleOriginRect.width / 2
        : rect.width / 2;
      const scaleOriginY = scaleOriginRect
        ? scaleOriginRect.top - rect.top + scaleOriginRect.height / 2
        : rect.height / 2;
      const maximumScaleDistance = Math.max(
        Math.hypot(scaleOriginX, scaleOriginY),
        Math.hypot(rect.width - scaleOriginX, scaleOriginY),
        Math.hypot(scaleOriginX, rect.height - scaleOriginY),
        Math.hypot(rect.width - scaleOriginX, rect.height - scaleOriginY),
        1
      );

      this.cards.forEach((card) => {
        let x = mod(card.layout.baseX + this.pan.x + this.cell, this.worldW) - this.cell;
        let y = mod(card.layout.baseY + this.pan.y + this.rowStep, this.worldH) - this.rowStep;
        let width = card.layout.width;
        let height = card.layout.height;
        let scale = 1;

        if (this.active && card !== this.active && this.transitionReady && this.pushProgress > 0) {
          const distance = Math.hypot(
            x + width / 2 - scaleOriginX,
            y + height / 2 - scaleOriginY
          );
          const distanceLead = Math.pow(clamp(distance / maximumScaleDistance, 0, 1), .75);
          // Distant objects start shrinking immediately. Objects close to the
          // selected product retain their size until later in the same push.
          const shrinkStart = .42 * (1 - distanceLead);
          const shrinkProgress = clamp(
            (wave - shrinkStart) / Math.max(.001, 1 - shrinkStart),
            0,
            1
          );
          const shrinkAmount = .18 + .27 * distanceLead;
          scale = 1 - shrinkProgress * shrinkAmount;

          const pushed = this.pushedPosition(card, x, y, width, height, expanded);
          x += (pushed.x - x) * this.pushProgress;
          y += (pushed.y - y) * this.pushProgress;
        }

        // A modulo wrap is a discontinuity, not a movement. Detect it from
        // consecutive targets and teleport only that off-screen object. This
        // keeps normal inertia while preventing a wrapped copy from easing
        // across the viewport as if it were a new row filling the space.
        // pushProgress already carries the same GSAP easing as the selected
        // image stage. Avoid a second position ease while the product view is
        // active, otherwise the wall arrives later than the image expansion.
        const spatialEase = !this.active && Date.now() < this.settlingUntil;
        const wrappedX = Math.abs(x - card.targetX) > this.worldW / 2;
        const wrappedY = Math.abs(y - card.targetY) > this.worldH / 2;
        if (spatialEase) {
          card.renderX = wrappedX ? x : card.renderX + (x - card.renderX) * ease;
          card.renderY = wrappedY ? y : card.renderY + (y - card.renderY) * ease;
          card.renderW += (width - card.renderW) * ease;
          card.renderH += (height - card.renderH) * ease;
          card.renderScale += (scale - card.renderScale) * ease;
        } else {
          // The pan value is already eased. Rendering the repeating field from
          // it directly keeps every row locked together with no trailing copy.
          card.renderX = x;
          card.renderY = y;
          card.renderW = width;
          card.renderH = height;
          card.renderScale = scale;
        }
        card.targetX = x;
        card.targetY = y;
        card.targetScale = scale;
        card.element.style.width = `${card.renderW}px`;
        card.element.style.height = `${card.renderH}px`;
        card.element.style.transform = `translate3d(${card.renderX}px, ${card.renderY}px, 0) scale(${card.renderScale})`;
      });

      this.raf = requestAnimationFrame(this.tick);
    }

    destroy() {
      this.introTimeline?.kill();
      this.navTimeline?.kill();
      this.contentTimeline?.kill();
      this.imageFlip?.kill();
      this.searchReveal?.kill();
      this.searchTimeline?.kill();
      if (this.active) this.restoreProductImage(this.active);
      this.flipLayer?.remove();
      this.stopGalleryGlide();
      cancelAnimationFrame(this.raf);
      clearTimeout(this.resizeTimer);
      clearTimeout(this.searchTimer);
      clearTimeout(this.dialogFocusTimer);
      clearTimeout(this.cartStatusTimer);
      cancelAnimationFrame(this.mobilePurchaseRaf);
      window.removeEventListener('resize', this.onResize);
      window.removeEventListener('popstate', this.onPopState);
      this.viewport.removeEventListener('pointermove', this.onPointerMove);
      this.productView?.removeEventListener('keydown', this.onProductViewKeyDown);
      this.productView?.removeEventListener('scroll', this.onProductViewScroll);
      this.viewStage?.removeEventListener('wheel', this.onGalleryWheel);
      this.viewStage?.removeEventListener('scroll', this.onGalleryScroll);
      this.viewStage?.removeEventListener('pointermove', this.onGalleryPointerMove);
      this.setProductDialogState(false);
    }
  }

  const boot = (scope = document) => {
    scope.querySelectorAll(SELECTOR).forEach((root) => {
      if (!root.infiniteProductField) root.infiniteProductField = new InfiniteProductField(root);
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot());
  else boot();
  document.addEventListener('shopify:section:load', (event) => boot(event.target));
})();
